import React, { createContext, useContext, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

const TopBarContext = createContext()

export function TopBarProvider({ children }) {
  const [topBarElements, setTopBarElements] = useState({})

  const value = useMemo(() => ({
    topBarElements,
    setTopBarElements,
  }), [topBarElements])

  return (
    <TopBarContext.Provider value={value}>
      {children}
    </TopBarContext.Provider>
  )
}

export function useTopBar() {
  const context = useContext(TopBarContext)
  if (!context) {
    throw new Error('useTopBar must be used within a TopBarProvider')
  }
  return context
}

// Component that pages can use to declare their TopBar content
export function TopBarContent({ children, className, ...props }) {
  const { setTopBarElements } = useTopBar()

  // Smart child detection - find specific components by displayName
  const childArray = React.Children.toArray(children)
  const titleComponent = childArray.find(child =>
    React.isValidElement(child) && child.type?.displayName === 'TopBar.Title',
  )
  const tabsComponent = childArray.find(child =>
    React.isValidElement(child) && child.type?.displayName === 'TopBar.Tabs',
  )
  const actionsComponent = childArray.find(child =>
    React.isValidElement(child) && child.type?.displayName === 'TopBar.Actions',
  )

  // Determine if we have tabs for layout
  const hasActualTabs = !!tabsComponent
  const hasTitle = !!titleComponent

  // Create the layout structure
  const layoutContent = (
    <div
      className={cn(
        'flex flex-col',
        hasActualTabs ? 'gap-3 py-3' : 'py-4',
        className,
      )}
      {...props}
    >
      {hasActualTabs
        ? (
            <>
              {/* Title row */}
              <div className="flex min-h-[30px] items-center justify-between">
                {titleComponent}
              </div>
              {/* Tabs and actions row */}
              <div className="flex min-h-[24px] items-center justify-between">
                <div className="flex items-center">
                  {tabsComponent}
                </div>
                <div className="flex items-center">
                  {actionsComponent}
                </div>
              </div>
            </>
          )
        : (
      // Single row layout when no tabs
            <div className={cn('flex h-[30px] items-center justify-between', hasTitle
              ? `justify-between`
              : `justify-end`)}
            >
              {titleComponent}
              {actionsComponent}
            </div>
          )}
    </div>
  )

  React.useEffect(() => {
    // Pass the structured layout content to the TopBar
    setTopBarElements({ content: layoutContent })

    return () => setTopBarElements({})
  }, [layoutContent, setTopBarElements])

  // Don't render anything - this is just for declaring TopBar content
  return null
}
